import { useEffect, useState } from 'react';
import axios from 'axios';
import QRCode from 'react-qr-code';
import { createResource,getResources,deleteResource, updateResource} from '../../../router/api';

/* ================= TYPES ================= */

interface Session {
  id: string;
  user_agent: string;
  ip_address: string;
  last_activity: string;
  is_current: boolean;
}

/* ================= COMPONENT ================= */

export default function SecuritySettings() {
  /* ================= STATE ================= */

  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  /* Password */
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [savingPassword, setSavingPassword] = useState(false);

  /* 2FA */
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [showQRCode, setShowQRCode] = useState(false);
  const [twoFactorSecret, setTwoFactorSecret] = useState('');
  const [twoFactorCode, setTwoFactorCode] = useState('');

  /* Sessions */
  const [sessions, setSessions] = useState<Session[]>([]);

  /* ================= LOAD ================= */

  useEffect(() => {
    loadSecurityData();
  }, []);

  const loadSecurityData = async () => {
    try {
      setLoading(true);

      const [securityRes, sessionsRes] = await Promise.all([
        getResources('user/security'),
        getResources('user/sessions'),
      ]);

      setTwoFactorEnabled(securityRes.data.two_factor_enabled);
      setSessions(sessionsRes.data.sessions);
    } catch {
      setMessage({ type: 'error', text: 'Impossible de charger les paramètres de sécurité.' });
    } finally {
      setLoading(false);
    }
  };

  /* ================= PASSWORD ================= */

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      return setMessage({ type: 'error', text: 'Les mots de passe ne correspondent pas.' });
    }

    try {
      setSavingPassword(true);
      setMessage(null);

      await createResource('user/password', {
        current_password: passwordForm.currentPassword,
        password: passwordForm.newPassword,
        password_confirmation: passwordForm.confirmPassword,
      });

      setMessage({ type: 'success', text: 'Mot de passe mis à jour avec succès.' });
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (err: any) {
      setMessage({
        type: 'error',
        text: err.response?.data?.message || 'Erreur lors du changement de mot de passe.',
      });
    } finally {
      setSavingPassword(false);
    }
  };

  /* ================= 2FA ================= */

  const toggle2FA = async () => {
    if (twoFactorEnabled) {
      if (!confirm('Voulez-vous désactiver la 2FA ?')) return;

      try {
        await createResource('user/2fa/disable', {});
        setTwoFactorEnabled(false);
        setMessage({ type: 'success', text: '2FA désactivée.' });
      } catch {
        setMessage({ type: 'error', text: 'Erreur lors de la désactivation de la 2FA.' });
      }
    } else {
      try {
        const res = await createResource('user/2fa/enable', {});
        setTwoFactorSecret(res.data.secret);
        setShowQRCode(true);
      } catch {
        setMessage({ type: 'error', text: 'Erreur lors de l’activation de la 2FA.' });
      }
    }
  };

  const confirm2FA = async () => {
    try {
      await createResource('user/2fa/confirm', {
        code: twoFactorCode.replace(/\s/g, ''),
      });

      setTwoFactorEnabled(true);
      setShowQRCode(false);
      setTwoFactorCode('');
      setTwoFactorSecret('');
      setMessage({ type: 'success', text: '2FA activée avec succès.' });
    } catch {
      setMessage({ type: 'error', text: 'Code invalide ou expiré.' });
    }
  };

  /* ================= SESSIONS ================= */

  const terminateSession = async (id: string) => {
    if (!confirm('Terminer cette session ?')) return;

    try {
      await deleteResource(`user/sessions/`,id);
      setSessions(prev => prev.filter(s => s.id !== id));
    } catch {
      setMessage({ type: 'error', text: 'Impossible de terminer la session.' });
    }
  };

  const terminateOtherSessions = async () => {
    if (!confirm('Terminer toutes les autres sessions ?')) return;

    try {
      await deleteResource('user/sessions/others', '');
      setSessions(prev => prev.filter(s => s.is_current));
    } catch {
      setMessage({ type: 'error', text: 'Erreur lors de la fermeture des sessions.' });
    }
  };

  const formatDevice = (ua: string) => {
    if (ua.includes('Mobile')) return 'Mobile';
    if (ua.includes('Chrome')) return 'Chrome';
    if (ua.includes('Firefox')) return 'Firefox';
    if (ua.includes('Safari')) return 'Safari';
    return 'Appareil inconnu';
  };

  /* ================= UI ================= */

  if (loading) {
    return <div className="py-12 text-center">Chargement…</div>;
  }

  return (
    <div className="space-y-8">

      {message && (
        <div className={`p-4 rounded ${
          message.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
        }`}>
          {message.text}
        </div>
      )}

      {/* PASSWORD */}
      <section className="bg-gray-50 p-6 rounded">
        <h3 className="font-semibold mb-4">Changer le mot de passe</h3>

        <form onSubmit={handlePasswordSubmit} className="space-y-3">
          {['currentPassword', 'newPassword', 'confirmPassword'].map(field => (
            <input
              key={field}
              type="password"
              placeholder={field === 'currentPassword' ? 'Mot de passe actuel' : field === 'newPassword' ? 'Nouveau mot de passe' : 'Confirmation'}
              value={(passwordForm as any)[field]}
              onChange={e => setPasswordForm(p => ({ ...p, [field]: e.target.value }))}
              required
              className="w-full border p-2 rounded"
            />
          ))}

          <button disabled={savingPassword} className="bg-blue-600 text-white px-4 py-2 rounded">
            {savingPassword ? 'Mise à jour…' : 'Mettre à jour'}
          </button>
        </form>
      </section>

      {/* 2FA */}
      <section className="bg-gray-50 p-6 rounded">
        <h3 className="font-semibold mb-3">Authentification à deux facteurs</h3>

        <p className="mb-3">
          Statut : <strong>{twoFactorEnabled ? 'Activée' : 'Désactivée'}</strong>
        </p>

        <button onClick={toggle2FA} className={`px-4 py-2 rounded text-white ${twoFactorEnabled ? 'bg-red-600' : 'bg-green-600'}`}>
          {twoFactorEnabled ? 'Désactiver' : 'Activer'}
        </button>

        {showQRCode && (
          <div className="mt-4 p-4 border rounded bg-white">
            <QRCode value={twoFactorSecret} size={160} />
            <input
              className="w-full mt-3 p-2 border rounded text-center"
              placeholder="123456"
              value={twoFactorCode}
              onChange={e => setTwoFactorCode(e.target.value)}
            />
            <button onClick={confirm2FA} className="w-full mt-3 bg-blue-600 text-white py-2 rounded">
              Confirmer
            </button>
          </div>
        )}
      </section>

      {/* SESSIONS */}
      <section className="bg-gray-50 p-6 rounded">
        <div className="flex justify-between mb-3">
          <h3 className="font-semibold">Sessions actives ({sessions.length})</h3>
          {sessions.length > 1 && (
            <button onClick={terminateOtherSessions} className="text-sm text-red-600">
              Terminer les autres
            </button>
          )}
        </div>

        {sessions.map(s => (
          <div key={s.id} className="bg-white p-3 rounded border flex justify-between mb-2">
            <div>
              <p>{formatDevice(s.user_agent)} {s.is_current && '(Actuelle)'}</p>
              <p className="text-xs text-gray-400">{s.last_activity}</p>
            </div>
            {!s.is_current && (
              <button onClick={() => terminateSession(s.id)} className="text-red-600 text-sm">
                Terminer
              </button>
            )}
          </div>
        ))}
      </section>
    </div>
  );
}
